import React from "react";
import { useNavigate } from "react-router-dom";
import { DataTable } from "@/components/ui/datatable";
import { auditsColumnConfig } from "@/config/tableColumnConfig";
import type { Audit } from "@/app/pages/auditcentre/index";
import type { ColumnDef } from "@tanstack/react-table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AuditCreateDialog } from "./AuditCreateDialog";
import { useTenancy } from "@/contexts/TenancyContext";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuLabel, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MoreHorizontal, Loader2, Search, Settings2 } from "lucide-react";
import { AuditService } from "@/app/pages/auditcentre/auditService";
import { TOUR_STEP_IDS } from '@/lib/tour-constants';

// --- In-table editing state ---
import { AuditEditDialog } from "./AuditEditDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

type AuditTableProps = {
    audits: Audit[];
    isLoading?: boolean;
    isCreateDialogOpen: boolean;
    setIsCreateDialogOpen: (open: boolean) => void;
    handleCreateAudit: (
        audit: Omit<Audit, 'id' | 'findings' | 'incidents'> & {
            findings: Array<Omit<Audit['findings'][number], 'id'>>;
            incidents: Array<Omit<Audit['incidents'][number], 'id'>>;
        }
    ) => void | string | Promise<void | string>;
    auditService: AuditService | null;
    onDelete?: (id: string) => void;
    onEdit?: (audit: Audit) => void;
};

const OwnersCell: React.FC<{ owners: Audit["owners"] }> = ({ owners }) => {
    const { userIdToName } = useTenancy();
    const asArray = Array.isArray(owners)
        ? owners.filter(Boolean)
        : owners
            ? [owners]
            : [];

    if (asArray.length === 0) {
        return <span>Unassigned</span>;
    }

    return <span>{asArray.map(owner => userIdToName[owner] || owner).join(", ")}</span>;
};

const AuditActionsCell: React.FC<{ audit: Audit; onEdit?: (audit: Audit) => void; onDelete?: (id: string) => void }> = ({ audit, onEdit, onDelete }) => {
    const navigate = useNavigate();
    return (
        <div id={TOUR_STEP_IDS.AUDITS_ACTION_BUTTONS}>
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="h-8 w-8 p-0">
                    <span className="sr-only">Open menu</span>
                    <MoreHorizontal className="h-4 w-4" />
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
                <DropdownMenuLabel className="text-gray-500">Actions</DropdownMenuLabel>
                <DropdownMenuItem onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/auditcentre/${audit.id}`);
                }}>
                    View
                </DropdownMenuItem>
                <DropdownMenuItem onClick={(e) => {
                    e.stopPropagation();
                    onEdit && onEdit(audit);
                }}>
                    Edit
                </DropdownMenuItem>
                <DropdownMenuItem
                    onClick={(e) => {
                        e.stopPropagation();
                        onDelete && onDelete(audit.id);
                    }}
                    className="text-red-600"
                >
                    Delete
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
        </div>
    );
};

const auditColumns = (onEdit?: (audit: Audit) => void, onDelete?: (id: string) => void): ColumnDef<Audit>[] => [
    {
        accessorKey: "type",
        header: "Type",
        cell: ({ row }) => {
            const type = row.original.type;
            const capitalized = type ? type.charAt(0).toUpperCase() + type.slice(1) : '';
            return <span>{capitalized}</span>;
        },
    },
    {
        // DED-1141: Changed from 'Scope' to 'Audit Scope'
        accessorKey: "scope",
        header: "Audit Scope",
        cell: ({ row }) => <span>{row.original.scope}</span>,
    },
    {
        accessorKey: "owners",
        header: "Owners",
        cell: ({ row }) => <OwnersCell owners={row.original.owners} />, 
    },
    {
        accessorKey: "auditor",
        header: "Auditor",
        cell: ({ row }) => <span>{row.original.auditor}</span>,
    },
    {
        id: "actions",
        header: "Actions",
        meta: { isAction: true },
        cell: ({ row }) => (
            <AuditActionsCell
                audit={row.original}
                onEdit={onEdit}
                onDelete={onDelete}
            />
        ),
    },
];

export function AuditTable({ audits, isLoading = false, isCreateDialogOpen, setIsCreateDialogOpen, handleCreateAudit, auditService, onDelete, onEdit }: AuditTableProps) {
    const [showDeleteDialog, setShowDeleteDialog] = React.useState(false);
    const [auditToDelete, setAuditToDelete] = React.useState<string | null>(null);
    
    // Filter states
    const [searchTerm, setSearchTerm] = React.useState('');
    const [typeFilter, setTypeFilter] = React.useState<'all' | string>('all');
    const [auditorFilter, setAuditorFilter] = React.useState<'all' | string>('all');
    const [showFilters, setShowFilters] = React.useState(false);
    
    // Extract unique types and auditors for filter options
    const auditTypes = React.useMemo(() => {
        const types = Array.from(new Set(audits.map(a => a.type).filter(Boolean)));
        return types;
    }, [audits]);
    
    const auditors = React.useMemo(() => {
        const uniqueAuditors = Array.from(new Set(audits.map(a => a.auditor).filter(Boolean)));
        return uniqueAuditors;
    }, [audits]);
    
    // Filter audits based on search and filter criteria
    const filteredAudits = React.useMemo(() => {
        return audits.filter(audit => {
            const matchesSearch = searchTerm
                ? (audit.scope?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                   audit.type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                   audit.auditor?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                   (Array.isArray(audit.owners) ? audit.owners.join(' ') : audit.owners)?.toLowerCase().includes(searchTerm.toLowerCase()))
                : true;
            
            const matchesType = typeFilter === 'all' ? true : audit.type === typeFilter;
            const matchesAuditor = auditorFilter === 'all' ? true : audit.auditor === auditorFilter;
            
            return matchesSearch && matchesType && matchesAuditor;
        });
    }, [audits, searchTerm, typeFilter, auditorFilter]);
    
    // Button style for active filter
    const getFilterButtonClass = (active: boolean) =>
        active
            ? 'filter-btn-active border font-semibold bg-primary-fade text-[color:var(--primary)] border-primary-fade-border'
            : 'border border-border bg-transparent text-foreground';

    const handleDeletePrompt = (id: string) => {
        setAuditToDelete(id);
        setShowDeleteDialog(true);
    };

    const handleDeleteConfirm = () => {
        if (auditToDelete && onDelete) {
            onDelete(auditToDelete);
        }
        setShowDeleteDialog(false);
        setAuditToDelete(null);
    };

    const handleDeleteCancel = () => {
        setShowDeleteDialog(false);
        setAuditToDelete(null);
    };

    const navigate = useNavigate();
    return (
        <>
            <AuditCreateDialog
                open={isCreateDialogOpen}
                onOpenChange={setIsCreateDialogOpen}
                onCreate={handleCreateAudit}
                auditService={auditService!}
            />
            
            {/* Add Button */}
            <div className="flex justify-end mb-3">
                <Button id={TOUR_STEP_IDS.CREATE_AUDIT_BUTTON} onClick={() => setIsCreateDialogOpen(true)}>Create Audit</Button>
            </div>

            {/* Search Bar with Inline Sliding Filters */}
            <div className="mb-4 relative">
                <div className="relative flex items-center gap-2 border shadow-xs rounded-lg bg-background overflow-hidden transition-all duration-300">
                    {/* Search Icon */}
                    <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 z-10">
                        <Search size={16} />
                    </div>
                    
                    {/* Search Input */}
                    <Input
                        className="flex-1 border-0 pl-10 pr-12 py-2.5 focus-visible:ring-0 focus-visible:ring-offset-0 shadow-none!"
                        placeholder="Search audits..."
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                    />
                    
                    {/* Filter Pills - Slide from right */}
                    <div 
                        className={`flex items-center gap-2 pr-12 py-2 transition-all duration-300 ease-in-out ${
                            showFilters 
                                ? 'opacity-100 translate-x-0 max-w-full' 
                                : 'opacity-0 translate-x-full max-w-0 overflow-hidden'
                        }`}
                    >
                        <Button
                            variant="outline"
                            size="sm"
                            className={getFilterButtonClass(typeFilter === 'all' && auditorFilter === 'all') + ' h-7 px-3 rounded-full text-xs whitespace-nowrap shadow-sm'}
                            onClick={() => {
                                setTypeFilter('all');
                                setAuditorFilter('all');
                            }}
                        >
                            All
                        </Button>

                        {/* Type Filter */}
                        <Select value={typeFilter} onValueChange={v => setTypeFilter(v)}>
                            <SelectTrigger className={getFilterButtonClass(typeFilter !== 'all') + ' h-7 min-w-[90px] rounded-full text-xs border-0 shadow-sm'}>
                                <span className="text-xs">
                                    {typeFilter !== 'all' ? typeFilter : 'Type'}
                                </span>
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All</SelectItem>
                                {auditTypes.map(type => (
                                    <SelectItem key={type} value={type}>{type}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>

                        {/* Auditor Filter */}
                        <Select value={auditorFilter} onValueChange={v => setAuditorFilter(v)}>
                            <SelectTrigger className={getFilterButtonClass(auditorFilter !== 'all') + ' h-7 min-w-[90px] rounded-full text-xs border-0 shadow-sm'}>
                                <span className="text-xs">
                                    {auditorFilter !== 'all' ? auditorFilter : 'Auditor'}
                                </span>
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All</SelectItem>
                                {auditors.map(auditor => (
                                    <SelectItem key={auditor} value={auditor}>{auditor}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    
                    {/* Settings Button */}
                    <Button
                        variant="ghost"
                        size="sm"
                        className="absolute right-1 top-1/2 transform -translate-y-1/2 h-8 w-8 p-0 z-10"
                        onClick={() => setShowFilters(!showFilters)}
                    >
                        <Settings2 size={16} className={showFilters ? 'text-primary' : ''} />
                    </Button>
                </div>
            </div>
            <div className="relative">
                {isLoading && (
                    <div className="absolute inset-0 bg-background/80 backdrop-blur-sm z-10 flex items-center justify-center">
                        <div className="flex items-center gap-2">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            <span className="text-sm text-muted-foreground">Loading audits...</span>
                        </div>
                    </div>
                )}
                <DataTable 
                    columns={auditColumns(onEdit, handleDeletePrompt)} 
                    data={filteredAudits} 
                    pageSize={5} 
                    columnDisplayConfig={auditsColumnConfig} 
                    sortingId={TOUR_STEP_IDS.AUDITS_SORTING_BUTTONS}
                    onRowClick={(row) => { navigate(`/auditcentre/${row.id}`) }}
                />
            </div>

            <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
                <DialogContent className="bg-card">
                    <DialogHeader>
                        <DialogTitle>Delete Audit</DialogTitle>
                        <DialogDescription>
                            Are you sure you want to delete this audit? This action cannot be undone.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="flex justify-end gap-2 mt-4">
                        <Button variant="outline" onClick={handleDeleteCancel}>
                            Cancel
                        </Button>
                        <Button variant="destructive" onClick={handleDeleteConfirm}>
                            Delete
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
}
